import React, { useState } from 'react'
import { Box, Typography, TextField, Button, CircularProgress } from '@mui/material'
import { Lock, Users, Clock, BarChart3 } from 'lucide-react'

interface Props {
  onUnlock: (email: string) => Promise<void>
}

export default function EmailGate({ onUnlock }: Props): JSX.Element {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (): Promise<void> => {
    if (!email.trim()) return
    setLoading(true)
    await onUnlock(email)
    setLoading(false)
  }

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: '16px',
        overflow: 'hidden',
        mt: 2,
      }}
    >
      {/* Blurred preview of what's behind the gate */}
      <Box
        sx={{
          filter: 'blur(6px)',
          opacity: 0.4,
          pointerEvents: 'none',
          p: 2.5,
          backgroundColor: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '16px',
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1, height: 60, backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '10px' }} />
          <Box sx={{ flex: 1, height: 60, backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '10px' }} />
        </Box>
        <Box sx={{ height: 40, backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '10px', mb: 1.5 }} />
        <Box sx={{ height: 40, backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '10px', mb: 1.5 }} />
        <Box sx={{ height: 40, backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '10px' }} />
      </Box>

      {/* Overlay with email capture */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3,
        }}
      >
        <Box
          sx={{
            backgroundColor: 'rgba(15,23,42,0.95)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(168,85,247,0.3)',
            borderRadius: '16px',
            p: 3,
            textAlign: 'center',
            maxWidth: 400,
            width: '100%',
          }}
        >
          <Lock size={28} color="#a855f7" />
          <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '18px', mt: 1, mb: 0.5 }}>
            Unlock Full Report
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', mb: 2 }}>
            Enter your email to see:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start', mb: 2, pl: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Users size={14} color="#a855f7" />
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>Your top engaged followers</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BarChart3 size={14} color="#a855f7" />
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>Engagement by content type</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Clock size={14} color="#a855f7" />
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>Best posting times</Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { handleSubmit().catch(console.error) } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  borderRadius: '10px',
                  fontSize: '14px',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                  '&.Mui-focused fieldset': { borderColor: '#a855f7' },
                },
              }}
            />
            <Button
              onClick={() => { handleSubmit().catch(console.error) }}
              disabled={!email.trim() || loading}
              sx={{
                background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
                color: 'white',
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 600,
                px: 2.5,
                whiteSpace: 'nowrap',
              }}
            >
              {loading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Unlock'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
